import { KeyedObject } from 'bernie-core/dist/source';
import { AnalyticsStore, ExperimentStore, RouteStore, QueryObject } from 'bernie-core';
import { ActivityBannerType, ActivityDetailBasic, ActivityDetailOffers } from '../common/__generated__/api';
import { ActivityDetailStore, DetailCriteriaStore } from '../stores';
import { CurrentTicketCount } from '../components/activity-details/offer/ticket';
import { getIsoDateFromBexDate } from './date-helper';
import { isExperimentEnabled } from './experiment-helper';
import { OfferPriceBreakDownHelper, GetTotalPriceBreakdown } from './offer-pricebreakdown-helper';
import Banners = ActivityDetailBasic.Banners;

interface RegionDetails {
  location: string;
  rid: string;
}

export interface GetCurrentTicketCount {
  ticketType: ActivityDetailOffers.TicketTypes;
  currentCount: number;
}

export function getDateRange(availabilities: ActivityDetailBasic.Availabilities[]): KeyedObject | null {
  let dateRange = null;
  if (availabilities && availabilities.length > 0) {
    dateRange = {
      startDate: getIsoDateFromBexDate(availabilities[0].date),
      endDate: getIsoDateFromBexDate(availabilities[availabilities.length - 1].date)
    };
  }

  return dateRange;
}

export function getRegionDetails(activityDetailStore: ActivityDetailStore, query: QueryObject): null | RegionDetails {
  let regionDetails = null;
  if (query && (query.location || query.rid)) {
    regionDetails = {
      location: query.location,
      rid: query.rid
    };
  } else if (
    activityDetailStore &&
    activityDetailStore.activityDetail &&
    activityDetailStore.activityDetail.location &&
    activityDetailStore.activityDetail.location.event &&
    activityDetailStore.activityDetail.location.event.region
  ) {
    const locationDetails = activityDetailStore.activityDetail.location.event.region;
    regionDetails = {
      location: locationDetails.fullRegionName ? locationDetails.fullRegionName : '',
      rid: locationDetails.regionId
    };
  }

  return regionDetails;
}

export function updateBackToSearchISQuery(routeStore: RouteStore, detailCriteriaStore: DetailCriteriaStore): void {
  if (detailCriteriaStore && !detailCriteriaStore.isQueryStringUpdated()) {
    detailCriteriaStore.setQueryStringUpdated(true);
    const changedKeys = detailCriteriaStore.changedQueryKeys(
      routeStore.queryParams,
      detailCriteriaStore.getActivityId()
    );
    /**
      TODO: We should use detailQuery directly here, but it is having strange
      parameters in it on deeplink due to `detailQuery=query` in controller
    **/
    if (changedKeys && changedKeys.length > 0) {
      const detailQuery = detailCriteriaStore.detailQuery;
      const newParams = {
        location: detailQuery && detailQuery.location,
        startDate: detailQuery && detailQuery.startDate,
        endDate: detailQuery && detailQuery.endDate,
        swp: detailQuery && detailQuery.swp,
        rid: detailQuery && detailQuery.rid
      };
      routeStore.updateSearch(newParams, {}, true);
    }
  }
}

const getTotalPrice = (currentTicketCounts: CurrentTicketCount[]): GetTotalPriceBreakdown => {
  const offerPriceBreakDownHelper = OfferPriceBreakDownHelper(currentTicketCounts, { applied: false });

  return offerPriceBreakDownHelper.getTotalPriceBreakdown();
};

const getCurrentTicketCount = (offer: ActivityDetailOffers.Offers): GetCurrentTicketCount[] =>
  offer.ticketTypes.map((ticketType: ActivityDetailOffers.TicketTypes) => ({
    ticketType: ticketType,
    currentCount: ticketType.defaultSelectedCount
  }));

export function trackEventWhenStickyBarPriceMismatches(
  offers: ActivityDetailOffers.Offers[],
  leadPrice: number,
  analytics: AnalyticsStore
): void | boolean {
  if (offers && leadPrice) {
    const isPriceMatching = offers.some(offer => {
      const currentTicketCounts = getCurrentTicketCount(offer);
      const totalPrice = getTotalPrice(currentTicketCounts);

      return totalPrice && totalPrice.totalPrice === leadPrice;
    });

    if (offers.length > 0 && !isPriceMatching) {
      analytics.trackEvent('LX.IS.StickyBar.PriceMismatch', 'sticky-bar-lead-price-mismatch', analytics.omnitureData);
    }
  }
}

export const getStickyBarLeadPrice = (stickyBarModel: ActivityDetailBasic.LeadTicketView): number =>
  (stickyBarModel.ticket.price && stickyBarModel.ticket.price.lead.amount) || 0;

export const trackInventoryErrorEvent = (
  experimentStore: ExperimentStore,
  banners: Banners[],
  analytics: AnalyticsStore
): void => {
  if (!banners || !analytics || !experimentStore) {
    return;
  }

  const nextAvailableDateBannerPresent =
    banners && banners.some(banner => banner && banner.action && banner.action.rfrr === 'LX.IS.next.available.date');

  const inventoryErrorBannerPresent =
    banners && banners.some(banner => banner && banner.action === null && banner.type === ActivityBannerType.ALERT);

  const nextAvailableDateEnable = isExperimentEnabled(experimentStore, 'LX_IS_Next_Available_date_PWA');

  if (nextAvailableDateEnable && !nextAvailableDateBannerPresent && inventoryErrorBannerPresent) {
    analytics.trackEvent(
      'LX.IS.NextAvailableDate.NotFound',
      'next-available-date-not-found-inventory-error',
      analytics.omnitureData
    );
  }
};
